import {
    ConnectionKind,
    type ITridentResource,
    ModelConstants,
    FabricGatewayType,
    IResourceContext,
    ConnectionAuthKind,
    stringIsNullOrEmpty,
    IModel,
    Metamodel,
    AdapterContext,
    artifactCredentialConnections,
    format,
    artifactCredentialTypes
} from 'shell-models-lib';
import { IAzureResource } from 'app/interfaces/IAzureResource';
import { LoggerService } from 'app/logger/logger.service';
import { cloneDeep } from 'lodash';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { IFrameService } from '../iframe.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { convertStringToObject } from 'app/shared/utils/string.util';
import type { TridentArtifactTypeV2 } from '@trident/data-access-artifact';
import { IFrameProfile, iFrameCustomerProfiles } from '../iframe-customer-profiles';
import { WaitActivity } from 'app/model/pipeline/wait.activity';
import { Model } from 'app/model/backendmodel';
import { ExecuteDataFlowActivity } from 'app/model/pipeline/execute-dataflow.activity';
import { DataflowRefreshActivity } from 'app/model/pipeline/dataflow-refresh.activity';
import { GlobalVariables } from '../globals';
import { filter, lastValueFrom, take } from 'rxjs';
import { type LinkedServiceDynamicLoaderService } from 'app/registries/linked-service/linked-service-dynamic-loader.service';
import { type DataflowFormatDynamicLoaderService } from 'app/registries/dataflow/dataflow-format-dynamic-loader.service';

export function fabricArtifactTypeToAdfResourceType(artifactType: TridentArtifactTypeV2, subType: boolean): string {
    switch (artifactType) {
        case ModelConstants.lakehouseArtifactType:
            return !subType ? ModelConstants.linkedService : ModelConstants.lakehouseLinkedService;
        case ModelConstants.kustoDatabaseArtifactType:
            return !subType ? ModelConstants.linkedService : ModelConstants.kustoDatabaseLinkedService;
        case ModelConstants.dataWarehouseArtifactType:
            return !subType ? ModelConstants.linkedService : ModelConstants.dataWarehouseLinkedService;
        case ModelConstants.fabricSqlArtifactType:
            return !subType ? ModelConstants.linkedService : ModelConstants.fabricSqlLinkedService;
        case ModelConstants.sqlAnalyticsEndpointArtifactType:
            return !subType ? ModelConstants.linkedService : ModelConstants.sqlAnalyticsEndpointLinkedService;
        case ModelConstants.pipelineArtifactType:
            return ModelConstants.pipeline;
        case ModelConstants.dataflowArtifactType:
            return ModelConstants.pbiDataflow;
        case ModelConstants.synapseNotebookArtifactType:
            return ModelConstants.pbiNotebook;
        case ModelConstants.workspaceArtifactType:
            return ModelConstants.workspace;
        case ModelConstants.copyJobArtifactType:
            return ModelConstants.copyJob;
        case ModelConstants.mountedRelationalDatabaseArtifactType:
            return ModelConstants.mountedRelationalDatabaseLinkedService;
        case ModelConstants.eventStreamArtifactType:
            return !subType ? ModelConstants.linkedService : ModelConstants.eventStreamLinkedService;
        default:
            return undefined;
    }
}

export function adfActivityToFabricActivity(activity: Model.Activity): void {
    activity.description = format(stringResources.MigrationTemplateDescription, activity.type);
    activity.state = 'Inactive';
    activity.onInactiveMarkAs = 'Skipped';
    switch (activity.type) {
        case ExecuteDataFlowActivity.MODEL.name: {
            activity.type = DataflowRefreshActivity.MODEL.name;
            break;
        }
        default: {
            activity.type = WaitActivity.MODEL.name;
            if (activity.typeProperties) {
                delete activity.typeProperties;
            }
            break;
        }
    }
}

export async function isSupportedTridentResource(
    tridentResource: ITridentResource,
    contextData: IResourceContext
): Promise<boolean> {
    if (!tridentResource) {
        return false;
    }
    if (tridentResource.artifactType === 'Connection') {
        const services = {
            iframeService: await getServiceInstance<IFrameService>(contextData, 'IFrameService'),
            linkedServiceRegistryService: await getServiceInstance<LinkedServiceRegistryService>(
                contextData,
                'LinkedServiceRegistryService'
            ),
            logger: await getServiceInstance<LoggerService>(contextData, 'LoggerService')
        };
        if (__IS_FABRIC_MDF__) {
            // FIXME: This is a workaround.
            // Dataflow should post message to MDF only when it's initialized.
            // But there's no guarantee on the order of message posting and MDF initialization today, 
            // So we need to make sure required services are initialized before processing the message.
            const linkedServiceDynamicLoaderService = await getServiceInstance<LinkedServiceDynamicLoaderService>(
                contextData,
                'LinkedServiceDynamicLoaderService'
            );
            const dataflowFormatDynamicLoaderService = await getServiceInstance<DataflowFormatDynamicLoaderService>(
                contextData,
                'DataflowFormatDynamicLoaderService'
            );
            await lastValueFrom(linkedServiceDynamicLoaderService.isInitialized$.pipe(filter((val) => val), take(1)));
            await lastValueFrom(dataflowFormatDynamicLoaderService.isInitialized$.pipe(filter((val) => val), take(1)));
        }
        let authKind: ConnectionAuthKind;
        let connectionKind: ConnectionKind;
        let gatewayType: FabricGatewayType;
        try {
            const datasourceReference = JSON.parse(tridentResource.datasourceReference);
            connectionKind = datasourceReference.kind;
            authKind = tridentResource.credentialDetails?.credentialType;
            gatewayType = tridentResource.gatewayType;
            if (!authKind || !connectionKind) {
                return false;
            }

            // In MDF, allow Lakehouse and Warehouse connections when showFabricConnector is enabled
            if (__IS_FABRIC_MDF__ && GlobalVariables.showFabricConnector
                && (connectionKind === ConnectionKind.LAKEHOUSE || connectionKind === ConnectionKind.WARHOUSE)) {
                return true;
            }

            //Artifact dmts credential connection doesn't need metadata, only has model
            if (artifactCredentialConnections.includes(connectionKind)) {
                return true;
            }

            const linkedServiceMetadatas = (
                services.iframeService.connectionKindToLinkedServiceTypesMapping.get(connectionKind) ?? [
                    services.iframeService.currentCustomerProfile.connectionTypeToLinkedServiceMapping[connectionKind]
                ]
            )
                ?.filter(Boolean)
                .map((type) => services.linkedServiceRegistryService.getLinkedServiceConfiguration(type));
            // Special handling WebForPipeline connection.
            // connectionKindToLinkedServiceTypesMapping is generated from linkedServiceToConnectionTypeMapping and is incorrect for WebForPipeline connection.
            // Let's refactor this in https://msdata.visualstudio.com/Azure%20Data%20Factory/_workitems/edit/3025623
            if (connectionKind === 'WebForPipeline') {
                linkedServiceMetadatas.push(
                    services.linkedServiceRegistryService.getLinkedServiceConfiguration(
                        adfModelTypeMap.rest.linkedServiceType
                    )
                );
            }
            if (
                gatewayType === FabricGatewayType.Enterprise &&
                !linkedServiceMetadatas.some((metadata) => metadata.supportOPDG)
            ) {
                return false;
            } else if (
                gatewayType !== FabricGatewayType.Enterprise &&
                linkedServiceMetadatas.every((metadata) => metadata.opdgOnly)
            ) {
                return false;
            }
            const result = linkedServiceMetadatas.some(
                (metadata) =>
                    !metadata.supportedAuthKindsInTrident || metadata.supportedAuthKindsInTrident.includes(authKind)
            );
            return result;
        } catch (e) {
            services.logger.error('Failed to check isSupportedTridentResource for connection', e, {
                objectId: tridentResource.objectId,
                datasourceType: tridentResource.datasourceType,
                connectionKind,
                authKind,
                gatewayType
            });
            return false;
        }
    }
    return true;
}

export async function convertTridentResourceToAzureResource(
    tridentResource: ITridentResource,
    options: { logger: LoggerService; adapterContext: AdapterContext }
): Promise<IAzureResource> {
    if (!tridentResource) {
        return undefined;
    }

    const { logger, adapterContext } = options;
    const artifactType = tridentResource.artifactType;
    let resourceType: string;
    if (artifactType === 'Connection') {
        try {
            const datasourceReference = JSON.parse(tridentResource.datasourceReference);
            resourceType = datasourceReference.kind
                ? iFrameCustomerProfiles[IFrameProfile.TridentWorkspace].connectionTypeToLinkedServiceMapping[
                datasourceReference.kind as ConnectionKind
                ]
                : undefined;

            // In MDF, map Lakehouse/Warehouse connections to their linked service when showFabricConnector is enabled
            if (__IS_FABRIC_MDF__ && GlobalVariables.showFabricConnector && stringIsNullOrEmpty(resourceType)) {
                if (datasourceReference.kind === ConnectionKind.LAKEHOUSE) {
                    resourceType = adfModelTypeMap.lakehouse.linkedServiceType;
                } else if (datasourceReference.kind === ConnectionKind.WARHOUSE) {
                    resourceType = adfModelTypeMap.Warehouse.linkedServiceType;
                }
            }

            if (stringIsNullOrEmpty(resourceType)) {
                logger.warn('convertTridentResourceToAzureResource', 'Unknown connection type', {
                    objectId: tridentResource.objectId,
                    datasourceType: tridentResource.datasourceType,
                    kind: datasourceReference.kind
                });
                return undefined;
            } else if (adapterContext.adapterConfig?.convertToFabric && artifactCredentialTypes.includes(resourceType)) {
                resourceType = datasourceReference.kind as string;
            }
        } catch (e) {
            logger.error('convertTridentResourceToAzureResource', `Failed to parse connection kind: ${e}`, {
                objectId: tridentResource.objectId,
                datasourceType: tridentResource.datasourceType
            });
            return undefined;
        }
    } else if (artifactType === 'Workspace') {
        const workloadPayload = convertStringToObject(tridentResource.workloadPayload);
        return {
            ...tridentResource,
            id: tridentResource.objectId,
            name: tridentResource.displayName,
            properties: workloadPayload?.properties
        };
    } else if (artifactType === 'SparkJobDefinition' ||
        artifactType === 'Environment' ||
        artifactType === 'FunctionSet' ||
        artifactType === 'DBTItem' ||
        artifactType === ModelConstants.dataflowFabricArtifactType) {
        return {
            ...tridentResource,
            id: tridentResource.objectId,
            name: tridentResource.displayName,
            properties: tridentResource.extendedProperties,
            workspaceObjectId: tridentResource.workspaceObjectId,
            dataFactoryId: tridentResource.dataFactoryId
        };
    } else if (artifactType === ModelConstants.mountedRelationalDatabaseArtifactType) {
        const sourceType = tridentResource?.extendedProperties?.SourceType;

        // only do it for Generic Mirror not for other mirror types
        if (sourceType === ModelConstants.mountedRelationalDatabaseLinkedService) {
            resourceType = fabricArtifactTypeToAdfResourceType(artifactType, true);
        } else {
            return {
                ...tridentResource,
                id: tridentResource.objectId,
                name: tridentResource.displayName,
                properties: undefined, // its properties are stored elsewhere and can't be fetched from the usual listResource call,
                workspaceObjectId: tridentResource.workspaceObjectId,
                dataFactoryId: tridentResource.dataFactoryId
            };
        }
        // MNICK TODO: remove this when payload is updated
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } else if (__IS_FABRIC_MDF__ && (tridentResource as any)?.properties?.type === 'MappingDataFlow') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return { ...tridentResource, id: `dataflows/${(tridentResource as any)?.name ?? ''}` } as any;
    } else {
        resourceType = fabricArtifactTypeToAdfResourceType(artifactType, true);
        if (stringIsNullOrEmpty(resourceType)) {
            logger.error('convertTridentResourceToAzureResource', 'Unknown artifact type', {
                objectId: tridentResource.objectId,
                datasourceType: tridentResource.datasourceType,
                artifactType
            });
            return undefined;
        }
    }
    let model: IModel;
    try {
        model = Metamodel.modelByName(resourceType);
    } catch (e) {
        logger.error('convertTridentResourceToAzureResource', `Failed to get model: ${e}`);
        return undefined;
    }
    const adapter = adapterContext.isAsyncAdapter(model)
        ? adapterContext.asyncAdapterFor(model)
        : adapterContext.adapterFor(model);
    if (adapter == null) {
        logger.error('convertTridentResourceToAzureResource', 'Unknown resource type', {
            objectId: tridentResource.objectId,
            datasourceType: tridentResource.datasourceType,
            resourceType
        });
    }
    try {
        return await adapter?.toAzureResource(tridentResource);
    } catch (e) {
        logger.warn('convertTridentResourceToAzureResource', `Failed to convert to azure resource: ${e}`, {
            objectId: tridentResource.objectId,
            datasourceType: tridentResource.datasourceType,
            resourceType
        });
        return undefined;
    }
}

export function convertDataFactoryResourceToTridentResource(
    dataFactoryResource: Pick<IAzureResource, 'name' | 'objectId' | 'workspaceObjectId' | 'properties' | 'dataFactoryId'>
): Pick<ITridentResource, 'objectId' | 'displayName' | 'description' | 'workloadPayload'> {
    const tempDataFactoryResource = cloneDeep(dataFactoryResource);
    const tridentResource: Pick<ITridentResource, 'objectId' | 'displayName' | 'description' | 'workloadPayload'> = {
        displayName: tempDataFactoryResource.name,
        objectId: tempDataFactoryResource.objectId,
        workloadPayload: ''
    };
    // TODO: Verify if this line is still needed given displayName & objectId are already there.
    tempDataFactoryResource.name = tempDataFactoryResource.objectId;
    delete tempDataFactoryResource.objectId;
    if (tempDataFactoryResource.properties) {
        tridentResource.description = tempDataFactoryResource.properties.description;
        delete tempDataFactoryResource.properties.description;
        if (tempDataFactoryResource.properties.extraTridentInfo) {
            delete tempDataFactoryResource.properties.extraTridentInfo;
        }
        if (tempDataFactoryResource.workspaceObjectId) {
            delete tempDataFactoryResource.workspaceObjectId;
        }
    }

    // Fabric dataflows: host expects ONLY the inner dataflow payload string.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((tempDataFactoryResource as any).type === 'DataflowFabric' && typeof (tempDataFactoryResource as any).workloadPayload === 'string') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tridentResource.workloadPayload = (tempDataFactoryResource as any).workloadPayload;
        return tridentResource;
    }

    tridentResource.workloadPayload = JSON.stringify(tempDataFactoryResource);
    return tridentResource;
}
